module.exports = function init_page_enter_data($) {

  /** SITE SELECTION **************************************************************************/

  // Populated once ACF signals the form is ready
  let $site_select, $year_select;

  const form_ready = new Promise((resolve) => {
    acf.addAction('ready', () => {
      $site_select = $('#select-site-id select');
      $year_select = $('#select-year select');

      // Site and year are selected independently — picking a site can add
      // more years to the list (that site's own SiteYear years), but never
      // removes the recent/non-site years that were already there. If the
      // year that was selected before the site change is still valid for
      // the new site, keep it selected; otherwise fall back to the prompt
      // state. Either way, always round-trip through update_activity_summary()
      // (not just when the year survives) — that's what writes the new
      // site_id into the session; skipping it on a cleared year would leave
      // the session pointing at the old site until a year gets picked again.
      $site_select.on('select2:select select2:unselect', function () {
        const site_id   = $(this).val();
        const site_name = $(this).find('option:selected').text();

        update_site_info(site_id, site_name);
        clear_activity_summary();

        populate_available_years(site_id, (prev_year) => {
          const year_still_valid = prev_year && $year_select.find('option[value="' + prev_year + '"]').length > 0;
          $year_select.val(year_still_valid ? prev_year : '');
          show_loading();
          update_activity_summary();
        });
      });

      // select2:unselect fires when the year is cleared (its clear "x") —
      // needed so the summary (including "Activities at other locations")
      // gets re-rendered off the now-empty year instead of leaving stale
      // content in the DOM, same as the site select below.
      $year_select.on('select2:select select2:unselect', function () {
        show_loading();
        update_activity_summary();
      });

      // Strip ACF's form wrapper and submit button — selects are used as AJAX filters, not a form
      $('.select-site form .acf-form-fields').unwrap();
      $('.select-site .acf-form-fields .acf-form-submit').remove();

      resolve();
    });
  });

  // Once form is ready, restore prior session selection and load summary
  form_ready.then(() => {
    restore_session_selection();
  });

  /** EVENT LISTENERS *************************************************************************/

  // Page visibility restored (tab switch, etc.) → re-sync selection
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      restore_session_selection();
    }
  });

  /** HELPERS *********************************************************************************/

  // Populates the year selector with the union of the always-available
  // recent years, the user's own non-site years, and (when site_id is
  // given) that site's SiteYear years — see mlmp_get_available_years()
  // (functions/functions-ajax.php). Replaces the option list wholesale;
  // on_done(prev_year) is called afterward with whatever value was
  // selected before repopulating, so callers can decide whether to keep it.
  function populate_available_years(site_id, on_done) {
    $.post(mlmp_settings.ajax_url, {
      action: 'get_available_years',
      ajax_nonce: mlmp_settings.nonce,
      site_id: site_id || 0,
    }, (response) => {
      if (!response || response.hasOwnProperty('success')) return;

      const prev_year = $year_select.val();

      $year_select.find('option').not(':first').remove();
      // krsort() on the PHP side sorts these descending, but JS always
      // enumerates integer-like object keys in ascending numeric order
      // regardless of source order — re-sort here or the PHP ordering is
      // silently discarded.
      Object.entries(response)
        .sort(([a], [b]) => b - a)
        .forEach(([key, label]) => {
          $year_select.append($('<option>', { value: key, text: label }));
        });

      if (typeof on_done === 'function') {
        on_done(prev_year);
      }
    });
  }

  // Fetches the saved session selection and restores the site/year dropdowns.
  // Site and year restore independently — either can be present without the
  // other. Only shows the loading indicator and triggers a summary load
  // when a year is present.
  function restore_session_selection() {
    $.post(mlmp_settings.ajax_url, {
      action: 'get_plot_selection',
      ajax_nonce: mlmp_settings.nonce,
    }, (response) => {
      const site_id = (response && response.site_id) || 0;
      const year     = (response && response.year) || '';

      if (site_id) {
        // Update the site select display without triggering our select2:select listener
        $site_select.val(site_id).trigger('change');
        update_site_info(site_id, $site_select.find('option:selected').text());
      }

      if (year) {
        show_loading();
      }

      populate_available_years(site_id, () => {
        if (year) {
          // Explicitly set the value — select2 does not pick up the DOM selected attribute
          $year_select.val(year);
          update_activity_summary();
        }
      });
    });
  }

  function update_activity_summary() {
    const site_id   = $site_select.val();
    const site_name = $site_select.find('option:selected').text();
    const year      = $year_select.val();
    const page_id   = (($('body').attr('class') || '').match(/page-id-(\d+)/) || [])[1] || 0;

    $.post(mlmp_settings.ajax_url, {
      action: 'load_activity_summary',
      ajax_nonce: mlmp_settings.nonce,
      selected_site_id: site_id,
      selected_site_name: site_name,
      selected_year: year,
      page_id: page_id,
    }, (response) => {
      if (response) {
        const $summary = $('.activity-summary');
        $summary.html(response.content);
        // JS owns visibility — d-none is not defined in this theme's CSS
        const has_rows = $summary.find('.summary-row').length > 0;
        $summary.find('.prompt-heading').toggle(!has_rows);
        $summary.find('.summary-heading').toggle(has_rows);
      }
    }).always(() => {
      hide_loading();
    });
  }

  function clear_activity_summary() {
    // Restore the prompt-only state rather than leaving the div empty
    const $summary = $('.activity-summary');
    $summary.find('.summary-heading').hide();
    $summary.find('.summary-row').remove();
    // Non-site activities are rendered as their own wrapper (heading + row),
    // not a bare .summary-row — clearing rows above doesn't touch it.
    $summary.find('.nonsite-activities').remove();
    $summary.find('.prompt-heading').show();
  }

  // Shows the site info panel, sets the site name, and appends ?site_id= to the link.
  function update_site_info(site_id, site_name) {
    const $panel = $('.site-info');
    if (!site_id || site_id == 0) {
      $panel.hide();
      return;
    }
    const base_url = $panel.find('.site-info-link').data('base-url') || '#';
    $panel.find('.site-info-name').text(site_name);
    $panel.find('.site-info-link').attr('href', base_url + '?site_id=' + site_id);
    $panel.show();
  }

  function show_loading() {
    $('.ajax-loading').show();
    $('.activity-summary').find('.summary-heading, .summary-row, .prompt-heading').hide();
  }

  function hide_loading() {
    $('.ajax-loading').hide();
  }

}
